<template>
  <div class="sidebar-header">
    <div
      class="sidebar-header__content"
      :class="{ 'sidebar-header__content--minimized': uiStore.sidebarMinimized }"
    >
      <div
        v-if="teamStore.currentTeam"
        class="sidebar-header__content__profile"
        tabindex="0"
        role="button"
        @click.stop="toggleOverlay"
        @keydown.enter="toggleOverlay"
        @keydown.space.prevent="toggleOverlay"
      >
        <ToolTip placement="bottom-start">
          <template #content="{ contentClass }">
            <p :class="contentClass">
              {{ teamStore.currentTeam.name }}
            </p>
          </template>
          <DarwinAvatar
            :id="teamStore.currentTeam.id"
            class="sidebar-header__content__profile-avatar"
            :name="teamStore.currentTeam.name"
            :url="avatarUrl"
            :has-tooltip="false"
          />
        </ToolTip>
        <div
          ref="teamNameLabel"
          class="sidebar-header__content__profile-name"
        >
          <span
            class="sidebar-header__content__profile-name-label"
            data-test="sidebar-team-name"
          >
            {{ teamStore.currentTeam.name }}
          </span>
        </div>
      </div>
      <NotificationModal
        v-if="!uiStore.sidebarMinimized && authStore.authenticated"
        class="sidebar-header__content__bell"
      />
    </div>

    <SidebarOverlay
      v-if="overlay && teamStore.currentTeam"
      v-click-outside="hideOverlay"
      class="sidebar-overlay"
      @close="overlay = false"
    />
  </div>
</template>

<script lang="ts" setup>
import DarwinAvatar from '@/uiKit/Avatar/DarwinAvatar.vue'
import NotificationModal from '@/components/Layout/Notification/NotificationModal.vue'
import SidebarOverlay from '@/components/Layout/Sidebar/SidebarOverlay.vue'

import { useTeamStore } from '@/pinia/useTeamStore'
import { useAuthStore } from '@/modules/Auth/useAuthStore'
import ToolTip from '@/uiKit/ToolTip/ToolTip.vue'
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useUIStore } from '@/pinia/useUIStore'

const authStore = useAuthStore()
const teamStore = useTeamStore()
const uiStore = useUIStore()

const overlay = ref(false)

const avatarUrl = computed(
  () => teamStore.currentTeam?.image?.thumbnail_url || teamStore.currentTeam?.image?.url || null,
)

const toggleOverlay = (): void => {
  overlay.value = !overlay.value
}

const hideOverlay = (): void => {
  overlay.value = false
}

const route = useRoute()
watch(
  () => route.query.settings as string | undefined,
  (tab) => {
    if (tab) {
      hideOverlay()
    }
  },
)
</script>

<style lang="scss" scoped>
$size: 32px;
$iconSize: 20px;
$gap: 8px;

.sidebar-header {
  max-height: #{$size * 2};

  &__content {
    position: relative;
    @include row;
    margin: 13px 15px 18px 15px;
    gap: $gap;

    &--minimized {
      display: block;
      width: $size;
      height: $size;

      .sidebar-header__content__profile {
        &-avatar {
          display: block;
          width: $size;
          height: $size;
          max-width: $size;
          min-width: $size;

          :deep(.responsive-avatar__icon) {
            width: $size;
          }
        }

        &-name {
          display: none;
        }
      }
    }

    &__profile {
      @include row;
      align-items: center;
      flex: 1 1 auto;
      height: $size;
      max-width: calc(100% - #{$size + $gap});
      gap: $gap;
      cursor: pointer;

      &:hover {
        .sidebar-header__content__profile-avatar,
        .sidebar-header__content__profile-name {
          opacity: 0.7;
        }
      }

      &:active {
        opacity: 0.5;
      }

      &-avatar {
        @include noSelect;
        width: $size;
        height: $size;
        border-radius: 50%;
      }

      &-name {
        @include row;
        align-items: center;
        max-width: calc(100% - #{$size + $gap});
        cursor: pointer;
        @include noSelect;

        &-label {
          @include typography(md-1, inter, 500);
          @include ellipsis(1, md-1);
          color: var(--color-text-default);
          cursor: pointer;
        }
      }
    }

    &__bell {
      @include row;
      align-items: center;
      min-width: $iconSize;
    }
  }
}
</style>
